import {
  ButtonIcon,
  Confirm,
  css,
  EditIcon,
  EllipsisText,
  Inline,
  Paragraph,
  Stack,
  styled,
  TrashIcon,
} from 'canoe';
import React, { useMemo, useState } from 'react';

import { ContentType } from '@candu-ai/content/types';
import {
  ContentPlacement,
  LOGIC_OPTIONS_TXT,
  LogicOptions,
  PAGE_OPTIONS_TXT,
  PageOptions,
  positioningOptions,
  showUntilOptions,
  triggerModalOptions,
  urlSelectOptions,
} from '@candu-ai/placement/types';
import { getDefaultOptionList } from '@candu-ai/placement/utils';
import { Snapshot } from '@candu-ai/snapshots/types';
import { useToggle } from 'commons/hooks';
import { isTooltipDocument } from 'craft/constants';

const LastPlacementDeleteTitle = 'Delete & Unpublish?';
const LastPlacementDeleteMessage =
  'Removing this Placement means users will no longer be able to see this Content. We advise Unpublishing the content at the same time as removing the Placement.';
const PlacementDeleteTitle = 'Remove Placement?';
const PlacementDeleteMessage =
  'Your Content is currently Published and Live. Your customers might be seeing the current Content. Removing it now will cause them to stop seeing the Content.';

const DetailsContainer = styled.div`
  min-width: 0;
  flex-shrink: 1;
`;

const ActionContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-shrink: 0;
    position: absolute;
    top: 0;
    right: 0;

    button {
      margin-left: ${theme.spaces.xs};
    }
  `}
`;

const Container = styled.div`
  margin-bottom: 0;
`;

const PlacementRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: nowrap;
    gap: ${theme.spaces.xs};
    position: relative;
  `}
`;

const DetailsRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: ${theme.spaces.xs};
  `}
`;

const Label = styled(Paragraph)`
  ${({ theme, mb }) => css`
    ${!mb &&
    css`
      margin-bottom: 0;
    `}

    max-width: none;
    font-weight: ${theme.fontWeights.bold};
    flex-shrink: 0;
  `}
`;

const Value = styled(Paragraph)`
  margin-bottom: 0;
  min-width: 0;
  max-width: none;
`;

const SnapshotThumbnail = styled.img`
  width: 328px;
  height: 187px;
  object-fit: contain;
`;

const getDeleteModalTitle = (isContentLive: boolean, isLastPlacement: boolean) => {
  if (!isContentLive) {
    return PlacementDeleteTitle;
  }

  if (isLastPlacement) {
    return LastPlacementDeleteTitle;
  }

  return PlacementDeleteTitle;
};

const getDeleteModalMessage = (isContentLive: boolean, isLastPlacement: boolean) => {
  if (!isContentLive) {
    return undefined;
  }

  if (isLastPlacement) {
    return LastPlacementDeleteMessage;
  }

  return PlacementDeleteMessage;
};

export interface ReadOnlyStateProps {
  setEditable?: (placement: any) => void;
  onDelete?: () => void;
  onDeleteAndUnpublish?: () => void;
  snapshot?: Snapshot;
  placement: ContentPlacement;
  isLast: boolean;
  context?: {
    maxPlacements?: number;
    askForEdit?: boolean;
    canDelete?: boolean;
    canEdit?: boolean;
  };
  isContentLive: boolean;
}

export const ReadOnlyState = ({
  snapshot,
  onDelete,
  onDeleteAndUnpublish,
  setEditable,
  placement,
  context,
  isLast,
  isContentLive,
}: ReadOnlyStateProps) => {
  const [askForEditOpen, setAskForEditOpen] = useState<boolean>(false);
  const selectedOption = useMemo(() => getDefaultOptionList(placement), [placement]);
  const [trashModalOpen, toggleTrashModal] = useToggle();
  const isModal = placement.type === ContentType.Modal;
  const isTooltip = useMemo(() => isTooltipDocument(placement.type), [placement]);

  const positioning = useMemo(
    () => !isModal && positioningOptions.find((item) => item.value === placement.location)?.label,
    [isModal, placement.location],
  );
  const frequency = useMemo(
    () => isModal && showUntilOptions.find((item) => item.value === placement.showTimeframe)?.label,
    [isModal, placement.showTimeframe],
  );

  return (
    <Container data-cy="readonly-placement">
      {trashModalOpen && (
        <Confirm
          severity="Danger"
          title={getDeleteModalTitle(isContentLive, isLast)}
          message={getDeleteModalMessage(isContentLive, isLast)}
          confirmButton={{
            children: isLast && isContentLive ? 'Delete Placement & Unpublish' : 'Confirm',
          }}
          closeButton={
            isLast && isContentLive
              ? {
                  children: 'Delete Placement',
                  onClick: onDelete,
                  variant: 'danger',
                  appearance: 'outline',
                }
              : undefined
          }
          withCheckbox={!isLast && isContentLive}
          onClose={toggleTrashModal}
          onConfirm={isLast && isContentLive ? onDeleteAndUnpublish : onDelete}
        />
      )}

      <PlacementRow>
        <DetailsContainer>
          <Inline limitChildrenWidth>
            {snapshot && (
              <SnapshotThumbnail
                alt={`Preview of ${snapshot?.pageUrl}`}
                src={snapshot.thumbnailLgS3Url}
              />
            )}
            <Stack spaceAfter="none" fullWidth>
              {selectedOption === PageOptions.EVERYWHERE && (
                <>
                  <Label mb="s">
                    {
                      PAGE_OPTIONS_TXT[
                        placement?.denyURLs?.length
                          ? PageOptions.MULTI_PAGE
                          : PageOptions.EVERYWHERE
                      ]
                    }
                  </Label>
                  {placement?.denyURLs && placement.denyURLs.length > 0 && (
                    <>
                      <Label>{LOGIC_OPTIONS_TXT[LogicOptions.ANY]} of:</Label>
                      <Stack space="none" spaceAfter="s" fullWidth>
                        {placement.denyURLs.map((url, index) => (
                          <DetailsRow key={`${index.toString()}`}>
                            <Label>URL does not match</Label>
                            <Value>
                              <EllipsisText content={url}>
                                <div>{url}</div>
                              </EllipsisText>
                            </Value>
                          </DetailsRow>
                        ))}
                      </Stack>
                    </>
                  )}
                </>
              )}

              {selectedOption === PageOptions.MULTI_PAGE && (
                <div>
                  <Label mb="s">{PAGE_OPTIONS_TXT[PageOptions.MULTI_PAGE]}</Label>
                  <Label>
                    {LOGIC_OPTIONS_TXT[placement.urlsLogicOperator || LogicOptions.ANY]} of:
                  </Label>
                  <Stack space="none" spaceAfter="s" fullWidth>
                    {(placement?.urls || []).map(({ operation, not }, index) => (
                      <DetailsRow key={`${index.toString()}`}>
                        <Label>
                          {
                            urlSelectOptions.find(
                              (item) => item.type === operation.type && (item.not === not || !not),
                            )?.label
                          }
                        </Label>
                        <Value>
                          <EllipsisText content={operation.operand}>
                            <div>{operation.operand}</div>
                          </EllipsisText>
                        </Value>
                      </DetailsRow>
                    ))}
                    {(placement?.denyURLs || []).map((url, index) => (
                      <DetailsRow key={`${index.toString()}`}>
                        <Label>URL does not match</Label>
                        <Value>
                          <EllipsisText content={url}>
                            <div>{url}</div>
                          </EllipsisText>
                        </Value>
                      </DetailsRow>
                    ))}
                  </Stack>
                </div>
              )}

              {!isModal && !isTooltip ? (
                <>
                  <DetailsRow>
                    <Label>HTML selector</Label>
                    <Value>
                      <EllipsisText content={placement.selector}>
                        <div>{placement.selector}</div>
                      </EllipsisText>
                    </Value>
                  </DetailsRow>

                  <DetailsRow>
                    <Label>Positioning</Label>
                    <Value>
                      <EllipsisText content={positioning}>
                        <div>{positioning}</div>
                      </EllipsisText>
                    </Value>
                  </DetailsRow>
                </>
              ) : null}

              {isModal ? (
                <>
                  <DetailsRow>
                    <Label>Display</Label>
                    <Value>
                      <EllipsisText content={triggerModalOptions[0]?.label}>
                        <div>{triggerModalOptions[0]?.label}</div>
                      </EllipsisText>
                    </Value>
                  </DetailsRow>

                  <DetailsRow>
                    <Label>Frequency</Label>
                    <Value>
                      <EllipsisText content={frequency}>
                        <div>{frequency}</div>
                      </EllipsisText>
                    </Value>
                  </DetailsRow>
                </>
              ) : null}
            </Stack>
          </Inline>
        </DetailsContainer>

        {setEditable || onDelete ? (
          <ActionContainer>
            {setEditable && (
              <ButtonIcon
                data-cy="set-editable-icon"
                color="contrast"
                icon={EditIcon}
                disabled={context?.canEdit === false}
                onClick={() => {
                  if (context?.askForEdit) {
                    setAskForEditOpen(true);
                  } else {
                    setEditable(true);
                  }
                }}
              />
            )}

            {onDelete && (
              <ButtonIcon
                disabled={context?.canDelete === false}
                color="contrast"
                icon={TrashIcon}
                onClick={toggleTrashModal}
              />
            )}
          </ActionContainer>
        ) : null}
      </PlacementRow>
      {askForEditOpen && (
        <Confirm
          severity="Info"
          title="Edit placement"
          message="Are you sure you want to edit this Placement? This will affect the experiment"
          confirmButton={{ children: `Edit placement` }}
          onClose={() => setAskForEditOpen(false)}
          onConfirm={() => {
            setEditable?.(true);
            setAskForEditOpen(false);
          }}
        />
      )}
    </Container>
  );
};
